/**
 * React Component Stimulus Controller
 * 
 * This controller bridges Rails/Phlex views with React components.
 * It handles mounting and unmounting React components into DOM elements
 * and manages the component lifecycle with Turbo navigation.
 * 
 * Usage in Rails/Phlex:
 *   <div data-controller="react-component"
 *        data-react-component-name-value="UserManagement"
 *        data-react-component-props-value='{"users": [...]}'>
 *   </div>
 * 
 * The component will be automatically mounted when the element connects
 * and unmounted when it disconnects (e.g., during Turbo navigation).
 */

import { Controller } from "@hotwired/stimulus"
import { registry } from "~/react"
import { createLogger } from "~/utils/logger"

const log = createLogger("ReactComponent")

export default class extends Controller {
  static values = {
    name: String,
    props: { type: Object, default: {} },
    hydrate: { type: Boolean, default: false }
  }

  // Persistent chrome (e.g. the sidebar) is marked data-turbo-permanent: Turbo
  // transfers the same DOM node across visits. For those we mount once and keep
  // the React tree alive across navigations instead of tearing it down and
  // rebuilding it on every visit (which causes a visible flash). Page-scoped
  // islands (no turbo-permanent) keep the original mount-per-visit behaviour.
  get isPermanent() {
    return this.element.hasAttribute("data-turbo-permanent")
  }

  connect() {
    this.boundBeforeCache = this.beforeCache.bind(this)
    document.addEventListener("turbo:before-cache", this.boundBeforeCache)
    // A permanent island that's already mounted (same node transferred by Turbo)
    // must not be re-mounted — that's the flash. Mount only on first connect.
    if (this.isPermanent && this.element.classList.contains("react-mounted")) {
      this.didConnect = true
      return
    }
    this.mountComponent()
    // Flag set *after* the initial mount so the value-changed callbacks
    // below skip Stimulus's initial fire (which uses the value's default as
    // previousValue, not undefined). Subsequent real changes still re-mount.
    this.didConnect = true
  }

  disconnect() {
    this.didConnect = false
    document.removeEventListener("turbo:before-cache", this.boundBeforeCache)
    // Keep permanent islands mounted across Turbo visits.
    if (this.isPermanent) return
    this.unmountComponent()
  }

  // Unmount before Turbo snapshots the page so Radix portals (Sheet overlays,
  // Dialogs, Popovers) rendered on document.body are cleaned up.  Without this,
  // the cached snapshot includes the portal DOM and restoring it leaves an
  // orphaned overlay that blocks all interaction. Permanent islands are exempt —
  // they persist across the visit and clean up their own portals on close.
  beforeCache() {
    if (this.isPermanent) return
    this.unmountComponent()
  }

  // Re-mount when props change (after connect has already run).
  propsValueChanged() {
    if (!this.didConnect) return
    if (this.element.isConnected) {
      this.mountComponent()
    }
  }

  // Re-mount when component name changes (rare but possible).
  nameValueChanged() {
    if (!this.didConnect) return
    if (this.element.isConnected) {
      this.mountComponent()
    }
  }

  mountComponent() {
    const componentName = this.nameValue
    
    if (!componentName) {
      log.error('No component name provided')
      return
    }

    // Check if the component is registered
    if (!registry.has(componentName)) {
      log.error(
        `Component "${componentName}" not found. ` +
        `Make sure it's registered in app/frontend/react/components/index.ts`
      )
      return
    }

    // Parse props - handle both JSON strings and objects
    let props = this.propsValue
    
    // If props came as a string (from HTML attribute), try parsing
    if (typeof props === 'string') {
      try {
        props = JSON.parse(props)
      } catch (e) {
        log.error('Failed to parse props:', e)
        props = {}
      }
    }

    // Mount the component
    const success = registry.mount(this.element, componentName, props, {
      hydrate: this.hydrateValue,
    })
    
    if (success) {
      this.element.classList.add('react-mounted')
    }
  }

  unmountComponent() {
    registry.unmount(this.element)
    this.element.classList.remove('react-mounted')
  }
}
